/* eslint-disable complexity */
import React, {FC, useEffect} from 'react';

import {cn} from '@bem-react/classname';
import {ArrowRotateRight, ArrowUpRightFromSquare, TriangleExclamation} from '@gravity-ui/icons';
import {Button, Icon, Text} from '@gravity-ui/uikit';
import {AppInstallation} from 'common/constants';
import {QuestionImageSize} from 'common/constants/survey';
import {isNil} from 'lodash';
import MarkdownText from 'ui/components/MarkdownText';
import {useFormUrl} from 'ui/hooks/useFormUrl';
import i18n from 'ui/i18n';
import * as Metrika from 'ui/utils/metrika';
import redirectToExternalUrl from 'ui/utils/redirectToExternalUrl';

import {SuccessMessageProps} from './SuccessMessage.types';

import './SuccessMessage.scss';

const {appInstallation} = window.YC;
export const cnSuccessMessage = cn('SuccessMessage');

const SuccessMessage: FC<SuccessMessageProps> = ({
    image,
    scores,
    subtitle,
    title,
    totalScores,
    redirect,
    buttonPaymentUrl,
    fillAgain,
}) => {
    const isQuiz = totalScores !== undefined;

    const urlToForm = useFormUrl();

    useEffect(() => {
        if (!redirect) return;

        let timer: ReturnType<typeof setTimeout>;
        if (redirect.with_delay) {
            timer = setTimeout(() => {
                if (redirect) {
                    redirectToExternalUrl(redirect.url, redirect.keep_iframe);
                }
            }, redirect.timeout);
        }

        if (redirect.button || redirect.with_delay) {
            Metrika.redirect(redirect.with_delay);
        }

        return () => {
            clearTimeout(timer);
        };
    }, [redirect]);

    const onClickRedirect = () => {
        Metrika.redirect(redirect?.with_delay, true);
    };

    const commonButtons = (
        <>
            {Boolean(buttonPaymentUrl) && (
                <Button
                    size="l"
                    href={buttonPaymentUrl}
                    target="_blank"
                    view="action"
                    className={cnSuccessMessage('PaymentButton')}
                >
                    {i18n('success-page', 'button-payment')}
                    <Button.Icon>
                        <ArrowUpRightFromSquare aria-hidden="true" focusable="false" />
                    </Button.Icon>
                </Button>
            )}
            {fillAgain !== false && (
                <Button
                    size="l"
                    view={redirect ? 'flat' : 'normal'}
                    href={urlToForm}
                    className={cnSuccessMessage('Button')}
                >
                    <Button.Icon>
                        <ArrowRotateRight aria-hidden="true" focusable="false" />
                    </Button.Icon>

                    {i18n('success-page', `fill-${isQuiz ? 'quiz' : 'form'}-again`)}
                </Button>
            )}
        </>
    );

    return (
        <section className={cnSuccessMessage()}>
            {image?.links && (
                <img
                    src={image?.links[QuestionImageSize.width720]}
                    className={cnSuccessMessage('Picture')}
                    alt=""
                />
            )}
            <div className={cnSuccessMessage('Wrapper')}>
                <div className={cnSuccessMessage('Texts')}>
                    {!isNil(scores) && (
                        <Text
                            variant="body-2"
                            color="secondary"
                            as="div"
                            className={cnSuccessMessage('Score')}
                        >
                            {i18n('success-page', 'scores', {
                                count: Number(scores),
                                scores: Number(scores),
                                totalScores: Number(totalScores),
                            })}
                        </Text>
                    )}
                    {title && (
                        <MarkdownText
                            text={title}
                            variant="header-2"
                            as="h1"
                            className={cnSuccessMessage('Title')}
                        />
                    )}

                    {subtitle && (
                        <MarkdownText
                            text={subtitle}
                            variant="body-2"
                            color="secondary"
                            as="div"
                            className={cnSuccessMessage('Subtitle')}
                        />
                    )}
                </div>

                {redirect ? (
                    <div className={cnSuccessMessage('RedirectBlock')}>
                        <div className={cnSuccessMessage('Buttons')}>
                            {!redirect.with_delay && (
                                <>
                                    <Button
                                        href={redirect.url}
                                        target={redirect.keep_iframe ? '_self' : '_top'}
                                        size="l"
                                        onClick={onClickRedirect}
                                        className={cnSuccessMessage('Button')}
                                        title={redirect.button}
                                        width="auto"
                                    >
                                        {
                                            <MarkdownText
                                                text={redirect.button}
                                                className={cnSuccessMessage('ButtonText')}
                                            />
                                        }
                                    </Button>
                                    {commonButtons}
                                </>
                            )}
                        </div>

                        {(redirect.with_delay || appInstallation === AppInstallation.Business) && (
                            <div className={cnSuccessMessage('RedirectWarning')}>
                                <Icon
                                    data={TriangleExclamation}
                                    size={20}
                                    className={cnSuccessMessage('RedirectWarningIcon')}
                                />
                                <Text
                                    variant="body-1"
                                    as="div"
                                    className={cnSuccessMessage('RedirectWarningText')}
                                >
                                    {i18n(
                                        'success-page',
                                        `${redirect.with_delay ? 'auto-' : ''}redirect-warning`,
                                        {
                                            redirectButtonText: redirect.button,
                                            url: redirect.url,
                                            timeout: Math.round(redirect.timeout / 1000),
                                        },
                                    )}
                                </Text>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className={cnSuccessMessage('Buttons')}>{commonButtons}</div>
                )}
            </div>
        </section>
    );
};

export default SuccessMessage;
